import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FilePenLine } from 'lucide-react'
import { DashboardLayout } from '../common/page_component/index.js'
import ListPageHeader from '../common/ListPageHeader.js'
import ListFilterPanel from '../common/ListFilterPanel.js'
import SearchSubmitInput from '../common/SearchSubmitInput.js'
import ListPagination from '../common/ListPagination.js'
import Button from '../common/Button.js'
import Alert from '../common/Alert.js'
import Loader from '../common/Loader.js'
import { useAuth } from '../../context/AuthContext'
import shipmentEditRequestService from '../../services/shipmentEditRequestService.js'
import { shipmentService } from '../../services/shipmentService.js'
import { listIdLinkClass, listIdLinkMobileClass, listPageWrapperClass } from '../common/uiClasses.js'
import { formatIndianDate, formatIndianTime } from '../../utils/localDate.js'
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js'
import { SHIPMENT_EDIT_FIELD_LABELS } from '../../utils/shipmentEditRequest.js'
import { formatReviewFieldValue, parcelDiffSummary } from '../../utils/shipmentEditRequestReview.js'
import ShipmentEditRequestReviewModal from './ShipmentEditRequestReviewModal.js'

const statusClass = (status) => {
  if (status === 'APPROVED') return 'bg-green-100 text-green-800 border-green-200'
  if (status === 'REJECTED') return 'bg-red-100 text-red-800 border-red-200'
  return 'bg-amber-100 text-amber-800 border-amber-200'
}

const filterInputClass = 'w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base'

const changeListSummary = (row) => {
  const keys = row.changed_fields || []
  return keys.map((key) => {
    if (key === 'parcels') {
      const { totalBefore, totalAfter, added, changed } = parcelDiffSummary(
        row.current_snapshot?.parcels,
        row.proposed_snapshot?.parcels,
      )
      const parts = [`Articles ${totalBefore} → ${totalAfter}`]
      if (added) parts.push(`${added} new`)
      if (changed) parts.push(`${changed} updated`)
      return parts.join(', ')
    }
    const label = SHIPMENT_EDIT_FIELD_LABELS[key] || key
    const proposed = formatReviewFieldValue(key, row.proposed_snapshot?.[key])
    return `${label}: ${proposed}`
  })
}

const ChangesList = ({ row, className = '' }) => {
  const lines = changeListSummary(row)
  if (!lines.length) return <span className="text-gray-400">—</span>
  return (
    <ul className={`list-disc pl-4 space-y-1 text-xs sm:text-sm break-words ${className}`}>
      {lines.map((line, idx) => (
        <li key={`${row.request_id}-line-${idx}`}>{line}</li>
      ))}
    </ul>
  )
}

const RequestCard = ({ row, canReview, onReview, onOpenShipment }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm border-l-4 border-l-blue-500 min-w-0">
    <div className="flex items-start justify-between gap-3 min-w-0">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-gray-900 break-all text-sm">{row.request_id}</p>
        <button
          type="button"
          className={`${listIdLinkMobileClass} block mt-1 text-left break-all`}
          onClick={() => onOpenShipment(row.shipment_id)}
        >
          #{row.shipment_id}
        </button>
        <p className="text-xs text-gray-500 mt-1">
          {formatIndianDate(row.created_at)}
          {formatIndianTime(row.created_at) ? ` · ${formatIndianTime(row.created_at)}` : ''}
        </p>
      </div>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border shrink-0 ${statusClass(row.status)}`}>
        {row.status}
      </span>
    </div>

    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm min-w-0">
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500">Agent</p>
        <p className="text-gray-900 break-words">{row.requested_by_name || row.requested_by}</p>
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500">Status at request</p>
        <p className="text-gray-900 break-words">{row.shipment_status_at_request}</p>
      </div>
    </div>

    <div className="mt-3 min-w-0">
      <p className="text-xs font-medium text-gray-500 mb-1">Requested changes</p>
      <ChangesList row={row} />
      {row.agent_remark ? (
        <p className="text-xs text-gray-600 mt-2 break-words">
          <span className="font-medium">Agent note:</span> {row.agent_remark}
        </p>
      ) : null}
    </div>

    <div className="mt-3 min-w-0">
      <p className="text-xs font-medium text-gray-500">Reviewer remark</p>
      <p className="text-sm text-gray-700 break-words mt-0.5">{row.reviewer_remark || '—'}</p>
    </div>

    {canReview && row.status === 'PENDING' ? (
      <Button variant="primary" className="mt-4 w-full sm:w-auto" onClick={() => onReview(row)}>
        Review
      </Button>
    ) : null}
  </div>
)

const ShipmentEditRequestList = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const canReview = user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant'
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [userId, setUserId] = useState('')
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [review, setReview] = useState(null)
  const [remark, setRemark] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const response = await shipmentEditRequestService.list({
        page,
        per_page: 20,
        status: status || undefined,
        search: search || undefined,
        user_id: canReview && userId ? userId : undefined,
      })
      const data = response.data?.data || []
      setRows((prev) => mergeLoadedPage(page, data, prev))
      setTotalPages(response.data?.pagination?.pages || 1)
      setTotalCount(response.data?.pagination?.total ?? data.length)
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not load detail requests')
      setRows([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [page, status, search, userId, canReview])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!review) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [review])

  useEffect(() => {
    if (!canReview) return undefined
    let active = true
    shipmentService.getDistinctUsers()
      .then((response) => {
        if (active) setUsers(response.data?.users || [])
      })
      .catch(() => {
        if (active) setUsers([])
      })
    return () => { active = false }
  }, [canReview])

  const openReview = (row) => {
    setReview(row)
    setRemark('')
  }

  const openShipment = (shipmentId) => {
    navigate(`/shipments/view/${encodeURIComponent(shipmentId)}`)
  }

  const approve = async () => {
    if (!review) return
    try {
      setSaving(true)
      const response = await shipmentEditRequestService.approve(review.request_id, { remark })
      Alert.success(response.data?.message || 'Shipment updated')
      setReview(null)
      setRows([])
      if (page === 1) await load()
      else setPage(1)
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not approve the request')
    } finally {
      setSaving(false)
    }
  }

  const reject = async () => {
    if (!review) return
    if (!remark.trim()) {
      Alert.error('Enter a remark for the agent')
      return
    }
    try {
      setSaving(true)
      const response = await shipmentEditRequestService.reject(review.request_id, { remark })
      Alert.success(response.data?.message || 'Request rejected')
      setReview(null)
      setRows([])
      if (page === 1) await load()
      else setPage(1)
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not reject the request')
    } finally {
      setSaving(false)
    }
  }

  const hasActiveFilters = Boolean(search || userId || status)

  const clearFilters = () => {
    setRows([])
    setSearch('')
    setUserId('')
    setStatus('')
    setPage(1)
  }

  const atEnd = page >= totalPages && !loading

  return (
    <DashboardLayout>
      <div className={`${listPageWrapperClass} overflow-x-hidden`}>
        <ListPageHeader title="Shipment edit requests" icon={FilePenLine} className="mb-2 sm:mb-3" />
        <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Search</label>
            <SearchSubmitInput
              value={search}
              onSearch={(value) => { setRows([]); setSearch(value); setPage(1) }}
              placeholder="Shipment or request id"
              ariaLabel="Search detail requests"
              inputClassName={`list-filter-panel login-input ${filterInputClass}`}
            />
          </div>
          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
            <select
              value={status}
              onChange={(event) => { setRows([]); setStatus(event.target.value); setPage(1) }}
              className={filterInputClass}
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
          {canReview ? (
            <div className="min-w-0">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">User</label>
              <select
                name="user_id"
                value={userId}
                onChange={(event) => { setRows([]); setUserId(event.target.value); setPage(1) }}
                className={filterInputClass}
              >
                <option value="">All Users</option>
                {users.map((person) => (
                  <option key={person.user_id} value={person.user_id}>
                    {person.location_hub_name ? `${person.name} (${person.location_hub_name})` : person.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </ListFilterPanel>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 w-full min-w-0 max-w-full overflow-hidden">
          {loading && page === 1 ? (
            <div className="flex items-center justify-center py-20">
              <Loader fullScreen={false} size="default" message="Loading detail requests..." />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="text-6xl mb-4">🔭</div>
              <p className="text-gray-600 text-lg font-semibold">No detail requests</p>
              <p className="text-gray-500 mt-2">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <div className="hidden lg:block w-full min-w-0">
                <table className="w-full table-fixed border-collapse text-xs xl:text-sm [&_th]:min-w-0 [&_td]:min-w-0">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="w-[15%] px-2 xl:px-3 py-3 text-left text-[11px] xl:text-xs font-medium text-gray-700 uppercase tracking-wide">Request</th>
                      <th className="w-[12%] px-2 xl:px-3 py-3 text-left text-[11px] xl:text-xs font-medium text-gray-700 uppercase tracking-wide">Shipment</th>
                      <th className="w-[12%] px-2 xl:px-3 py-3 text-left text-[11px] xl:text-xs font-medium text-gray-700 uppercase tracking-wide">Agent</th>
                      <th className="w-[10%] px-2 xl:px-3 py-3 text-left text-[11px] xl:text-xs font-medium text-gray-700 uppercase tracking-wide">At status</th>
                      <th className="w-[33%] px-2 xl:px-3 py-3 text-left text-[11px] xl:text-xs font-medium text-gray-700 uppercase tracking-wide">Requested changes</th>
                      <th className="w-[18%] px-2 xl:px-3 py-3 text-left text-[11px] xl:text-xs font-medium text-gray-700 uppercase tracking-wide">Review</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rows.map((row, index) => (
                      <tr key={row.request_id} className={`align-top ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/80'} hover:bg-gray-50`}>
                        <td className="px-2 xl:px-3 py-3 min-w-0">
                          <div className="font-medium text-gray-900 break-all">{row.request_id}</div>
                          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${statusClass(row.status)}`}>
                            {row.status}
                          </span>
                          <div className="text-[11px] text-gray-500 mt-1 break-words">
                            {formatIndianDate(row.created_at)}
                            {formatIndianTime(row.created_at) ? ` · ${formatIndianTime(row.created_at)}` : ''}
                          </div>
                        </td>
                        <td className="px-2 xl:px-3 py-3 min-w-0">
                          <button
                            type="button"
                            className={`${listIdLinkClass} break-all text-left`}
                            onClick={() => openShipment(row.shipment_id)}
                          >
                            {row.shipment_id}
                          </button>
                        </td>
                        <td className="px-2 xl:px-3 py-3 text-gray-900 break-words min-w-0">{row.requested_by_name || row.requested_by}</td>
                        <td className="px-2 xl:px-3 py-3 text-gray-800 break-words min-w-0">{row.shipment_status_at_request}</td>
                        <td className="px-2 xl:px-3 py-3 text-gray-700 min-w-0">
                          <ChangesList row={row} />
                          {row.agent_remark ? (
                            <p className="text-[11px] text-gray-500 mt-2 break-words">
                              <span className="font-medium">Note:</span> {row.agent_remark}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-2 xl:px-3 py-3 min-w-0">
                          <p className="text-gray-600 break-words text-xs">{row.reviewer_remark || '—'}</p>
                          {canReview && row.status === 'PENDING' ? (
                            <Button variant="primary" className="mt-2 !text-xs !px-3 !py-1.5" onClick={() => openReview(row)}>
                              Review
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="lg:hidden space-y-3 p-3 sm:p-4 min-w-0">
                {rows.map((row) => (
                  <RequestCard
                    key={row.request_id}
                    row={row}
                    canReview={canReview}
                    onReview={openReview}
                    onOpenShipment={openShipment}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {rows.length > 0 ? (
          <div className="w-full min-w-0 mt-1">
            <ListPagination
              currentPage={page}
              totalPages={totalPages}
              loading={loading && page > 1}
              onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            />
            {atEnd ? (
              <p className="text-center text-xs text-gray-500 pb-2">
                {totalCount > 0
                  ? `Showing ${rows.length} of ${totalCount} request${totalCount === 1 ? '' : 's'}`
                  : `Showing ${rows.length} request${rows.length === 1 ? '' : 's'}`}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {review ? (
        <ShipmentEditRequestReviewModal
          review={review}
          saving={saving}
          remark={remark}
          onRemarkChange={setRemark}
          onCancel={() => setReview(null)}
          onApprove={approve}
          onReject={reject}
        />
      ) : null}
    </DashboardLayout>
  )
}

export default ShipmentEditRequestList
